<script setup lang="ts">
    const props = withDefaults(
        defineProps<{
            modelValue: boolean;
            label?: string;
            tooltip?: string;
            id?: string;
            disabled?: boolean;
            modifier?: 'dark' | 'default';
            size?: 'xl' | 'l' | 'm' | 's';
            type?: 'default' | 'icon';
        }>(),
        {
            modifier: 'default',
            type: 'default',
        }
    );

    const emit = defineEmits<{
        (e: 'update:modelValue', value: boolean): void;
    }>();

    const toggle = () => {
        emit('update:modelValue', !props.modelValue);
    };

    // DS sizing — when size is set, use Figma DS dimensions
    const trackSizeClass = computed(() => {
        if (!props.size) return 'min-w-11 h-6';
        const map = { xl: 'w-[40px] h-[24px]', l: 'w-[36px] h-[22px]', m: 'w-[32px] h-[20px]', s: 'w-[26px] h-[16px]' };
        return map[props.size];
    });

    const thumbSizeClass = computed(() => {
        if (!props.size) return 'after:h-5 after:w-5 after:top-[2px] after:start-[2px]';
        const map = {
            xl: 'after:h-[18px] after:w-[18px] after:top-[3px] after:start-[3px]',
            l: 'after:h-4 after:w-4 after:top-[3px] after:start-[3px]',
            m: 'after:h-[14px] after:w-[14px] after:top-[3px] after:start-[3px]',
            s: 'after:h-[10px] after:w-[10px] after:top-[3px] after:start-[3px]',
        };
        return map[props.size];
    });

    const labelSizeClass = computed(() => {
        if (!props.size) return 'text-sm';
        const map = { xl: 'text-[16px]', l: 'text-[15px]', m: 'text-[14px]', s: 'text-[13px]' };
        return map[props.size];
    });

    // Icon type thumb: real DOM element instead of pseudo-element
    const iconThumbSizeClass = computed(() => {
        const map = { xl: 'size-[18px] text-[10px]', l: 'size-[18px] text-[9px]', m: 'size-[14px] text-[8px]', s: 'size-[10px] text-[7px]' };
        return map[props.size || 'l'];
    });

    const iconThumbIcon = computed(() => {
        return props.modelValue ? 'fa-solid fa-check' : 'fa-solid fa-xmark';
    });

    const iconThumbColor = computed(() => {
        return props.modelValue ? 'text-[#27272a]' : 'text-[#e4e4e7]';
    });

    // Checked color: DS uses gray-800 (dark), legacy uses yellow or black depending on modifier
    const checkedColorClass = computed(() => {
        if (props.size) {
            // DS mode: always gray-800
            return props.modelValue ? '!bg-[#27272a]' : '';
        }
        // Legacy mode
        if (props.modelValue && props.modifier === 'dark') return '!bg-black dark:!bg-black';
        if (props.modelValue) return '!bg-branding-brand-yellow dark:!bg-branding-brand-yellow';
        return '';
    });
</script>

<template>
    <label
        class="inline-flex items-center cursor-pointer"
        :class="disabled ? 'is-disabled' : ''"
    >
        <input
            :id="id"
            type="checkbox"
            class="sr-only peer"
            :checked="modelValue"
            :disabled="disabled"
            @change="toggle"
        />
        <!-- Icon type: real DOM thumb with check/cross -->
        <div
            v-if="type === 'icon' && size"
            class="relative rounded-full p-[3px] flex items-center transition-all"
            :class="[
                trackSizeClass,
                modelValue ? 'bg-[#27272a] justify-end' : 'bg-[#e4e4e7] justify-start',
            ]"
        >
            <div
                class="bg-white rounded-full shadow-[0px_1px_2px_rgba(0,0,0,0.25)] flex items-center justify-center shrink-0 transition-all"
                :class="iconThumbSizeClass"
            >
                <i
                    :class="[iconThumbIcon, iconThumbColor]"
                    class="leading-none"
                />
            </div>
        </div>
        <!-- Default type: CSS pseudo-element thumb -->
        <div
            v-else
            class="relative bg-[#e4e4e7] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-branding-green-light dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 after:content-[''] after:absolute after:bg-white after:shadow-[0px_1px_3px_rgba(0,0,0,0.1)] after:border-gray-300 after:border after:rounded-full after:transition-all dark:border-gray-600"
            :class="[
                trackSizeClass,
                thumbSizeClass,
                modelValue ? 'after:translate-x-full rtl:after:-translate-x-full after:border-white' : '',
                checkedColorClass,
            ]"
        ></div>

        <span
            :class="['pl-3 font-medium text-branding-mid-grey dark:text-gray-300', labelSizeClass]"
        >{{ label }}</span>
        <span
            v-if="tooltip"
            :data-tooltip="tooltip"
            class="ml-1 text-sm font-medium text-branding-mid-grey dark:text-gray-300"
        >
            <i class="fa-regular fa-circle-info" />
        </span>
    </label>
</template>

<style scoped>
    .is-disabled {
        @apply opacity-30 pointer-events-none;
    }
</style>
